import {
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  OfferingTypesEnum,
  OrderableRecordsWithSequence,
  PriceUpliftConfigFormSchema,
  PriceUpliftConfigFormType,
  PriceUpliftConfigResType,
  PriceUpliftConfigTypeEnum,
  PriceUpliftPercentageTypeEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { sortByProductPosition } from '@monetize/utils/core';
import isNil from 'lodash/isNil';
import { ONE_TIME_PRODUCT_TYPES } from '../constants/products';
import { logger } from '../services/logger';
import { getQuoteOfferingsWithChildren } from './quotes';

/** Offering types that never support renewal uplift. */
const UPLIFT_INELIGIBLE_OFFERING_TYPES = new Set<OfferingTypesEnum>([
  OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL,
  OfferingTypesEnum.MIN_COMMIT,
]);

/**
 * Whether an offering can carry a renewal uplift configuration at all. Shared
 * with the per-offering configuration drawer gate in QuoteOfferingActions so
 * the two can never disagree about which offerings are uplift-capable.
 */
export const canOfferingCarryPriceUplift = (
  billingFrequency: RateBillingFrequencyEnum | null | undefined,
  offeringType: OfferingTypesEnum | undefined,
) =>
  billingFrequency !== RateBillingFrequencyEnum.ONETIME &&
  !!offeringType &&
  !UPLIFT_INELIGIBLE_OFFERING_TYPES.has(offeringType);

/** Quote-wide summary of every renewal uplift configuration on the quote. */
export type QuoteUpliftSummary =
  | { status: 'none'; hasLockedConfig: boolean }
  | {
      status: 'uniform';
      amount: number;
      percentageType: PriceUpliftPercentageTypeEnum;
      /**
       * One percentage, but it does not reach every eligible product on the
       * quote — so the header must not read as a flat quote-wide uplift.
       */
      isPartial: boolean;
      hasLockedConfig: boolean;
    }
  | { status: 'variable'; hasLockedConfig: boolean };

export type UpliftEligibleOffering = {
  /** Owns the priceUpliftConfigurationId FK — always the save target. */
  parentQuoteOffering: IQuoteOfferingRespSchema;
  /** Last ramp segment when the offering is ramped, otherwise the parent. Drives item/PPU display. */
  displayQuoteOffering: IQuoteOfferingRespSchema;
  config: PriceUpliftConfigResType | null;
  /** Non-one-time product ids, position-sorted. */
  eligibleProductIds: string[];
  /**
   * PPU display precision per product id. It is a catalog-level product
   * setting that neither the quote item nor the rate response carries.
   */
  ppuDisplayPrecisionByProductId: Record<string, number>;
};

export type QuoteRenewalUpliftFormType = {
  mode: 'uniform' | 'individual';
  /** Header-level percentage, only meaningful in uniform mode. */
  amount: number | null;
  /** Header-level, applies to both modes — stamped onto every saved product config. */
  percentageType: PriceUpliftPercentageTypeEnum;
  offerings: {
    quoteOfferingId: string;
    products: {
      productId: string;
      included: boolean;
      amount: number | null;
    }[];
  }[];
};

export type QuoteUpliftPlanStep =
  | {
      action: 'create';
      quoteOfferingId: string;
      payload: PriceUpliftConfigFormType;
    }
  | {
      action: 'update';
      quoteOfferingId: string;
      priceUpliftConfigurationId: string;
      payload: PriceUpliftConfigFormType;
    }
  | {
      action: 'delete';
      quoteOfferingId: string;
      priceUpliftConfigurationId: string;
    };

/**
 * Whether a percentage actually uplifts anything. `0` and an empty input are
 * both "no uplift" everywhere in this feature — the summary, the save plan, and
 * the drawer's mode switch all have to agree on that.
 */
export const isUpliftAmount = (amount: unknown): amount is number =>
  typeof amount === 'number' && Number.isFinite(amount) && amount !== 0;

/** Renewal price multiplier for a given uplift percentage. */
export const getUpliftMultiplier = (amount?: number | null) =>
  1 + (amount ?? 0) / 100;

export const formatUpliftSummaryLabel = (summary: QuoteUpliftSummary) => {
  switch (summary.status) {
    case 'uniform': {
      const percentage =
        summary.percentageType === PriceUpliftPercentageTypeEnum.MAX
          ? `Up to ${summary.amount}%`
          : `${summary.amount}%`;
      return summary.isPartial ? `${percentage} (partial)` : percentage;
    }
    case 'variable':
      return 'Varies';
    default:
      return 'Configure';
  }
};

const getConfigForOffering = (
  quote: IQuoteRespSchema,
  quoteOffering: IQuoteOfferingRespSchema,
): PriceUpliftConfigResType | null => {
  const { priceUpliftConfigurationId } = quoteOffering;
  if (!priceUpliftConfigurationId) {
    return null;
  }

  const config = quote.priceUpliftConfigurations?.[priceUpliftConfigurationId];
  if (!config) {
    logger.warn(
      `Quote offering ${quoteOffering.id} references price uplift configuration ${priceUpliftConfigurationId}, but it is missing from the quote response`,
    );
    return null;
  }

  return config;
};

/**
 * Top-level quote offerings that can carry a renewal uplift configuration, each
 * paired with its existing configuration and the products that can be uplifted.
 */
export const getUpliftEligibleOfferings = (
  quote: IQuoteRespSchema,
  productOfferingsObj: OrderableRecordsWithSequence,
): UpliftEligibleOffering[] => {
  const offeringsWithChildren = getQuoteOfferingsWithChildren(
    quote.quoteOfferings ?? [],
  );

  const eligibleOfferings: UpliftEligibleOffering[] = [];

  offeringsWithChildren.forEach(({ quoteOffering, children }) => {
    if (!quoteOffering) {
      return;
    }

    const catalogOffering = productOfferingsObj.byId[quoteOffering.offeringId];
    if (
      !catalogOffering ||
      !canOfferingCarryPriceUplift(
        quoteOffering.billingFrequency,
        catalogOffering.type,
      )
    ) {
      return;
    }

    const displayQuoteOffering = children.at(-1) ?? quoteOffering;
    const eligibleProductIds = sortByProductPosition(
      displayQuoteOffering.items ?? [],
    )
      .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
      .map(({ productId }) => productId);

    if (!eligibleProductIds.length) {
      return;
    }

    eligibleOfferings.push({
      parentQuoteOffering: quoteOffering,
      displayQuoteOffering,
      config: getConfigForOffering(quote, quoteOffering),
      eligibleProductIds,
      ppuDisplayPrecisionByProductId: Object.fromEntries(
        (catalogOffering.products ?? []).flatMap(
          ({ id, ppuDisplayPrecision }) =>
            isNil(ppuDisplayPrecision)
              ? []
              : [[id, ppuDisplayPrecision] as const],
        ),
      ),
    });
  });

  return eligibleOfferings;
};

/** Every uplift {amount, percentageType} pair a configuration contributes. */
const getConfigUpliftEntries = (config: PriceUpliftConfigResType) => {
  if (config.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS) {
    return isUpliftAmount(config.amount)
      ? [
          {
            amount: config.amount,
            percentageType:
              config.percentageType ?? PriceUpliftPercentageTypeEnum.FIXED,
          },
        ]
      : [];
  }

  return (config.productConfigurations ?? [])
    .filter(({ amount }) => isUpliftAmount(amount))
    .map(({ amount, percentageType }) => ({
      amount,
      percentageType: percentageType ?? PriceUpliftPercentageTypeEnum.FIXED,
    }));
};

/** True when a configuration uplifts every one of the offering's products. */
const coversEveryProduct = ({
  config,
  eligibleProductIds,
}: UpliftEligibleOffering) => {
  if (!config) {
    return false;
  }

  if (config.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS) {
    return isUpliftAmount(config.amount);
  }

  const upliftedProductIds = new Set(
    (config.productConfigurations ?? [])
      .filter(({ amount }) => isUpliftAmount(amount))
      .map(({ productId }) => productId),
  );
  return eligibleProductIds.every((productId) =>
    upliftedProductIds.has(productId),
  );
};

/**
 * Summarizes renewal uplift across the whole quote.
 *
 * The percentage itself comes from the configurations alone, so one that
 * outlives its offering's catalog eligibility still counts. `eligibleOfferings`
 * is only consulted to tell a quote-wide percentage apart from one covering
 * part of the quote — a distinction the header label has to make.
 */
export const deriveQuoteUpliftSummary = (
  quote: IQuoteRespSchema,
  eligibleOfferings: UpliftEligibleOffering[],
): QuoteUpliftSummary => {
  const entries: {
    amount: number;
    percentageType: PriceUpliftPercentageTypeEnum;
  }[] = [];
  let hasLockedConfig = false;

  // A lock only freezes what the drawer can actually edit, so an ineligible
  // offering carrying a stale locked configuration must not gate the rest.
  const eligibleOfferingIds = new Set(
    eligibleOfferings.map(({ parentQuoteOffering }) => parentQuoteOffering.id),
  );

  (quote.quoteOfferings ?? [])
    .filter(({ parentQuoteOfferingId }) => !parentQuoteOfferingId)
    .forEach((quoteOffering) => {
      const config = getConfigForOffering(quote, quoteOffering);
      if (!config) {
        return;
      }
      hasLockedConfig =
        hasLockedConfig ||
        (config.locked && eligibleOfferingIds.has(quoteOffering.id));
      entries.push(...getConfigUpliftEntries(config));
    });

  if (!entries.length) {
    return { status: 'none', hasLockedConfig };
  }

  const [first] = entries;
  const isUniform = entries.every(
    ({ amount, percentageType }) =>
      amount === first.amount && percentageType === first.percentageType,
  );

  if (!isUniform) {
    return { status: 'variable', hasLockedConfig };
  }

  return {
    status: 'uniform',
    amount: first.amount,
    percentageType: first.percentageType,
    isPartial: !eligibleOfferings.every(coversEveryProduct),
    hasLockedConfig,
  };
};

const getConfigPercentageType = (
  config: DesiredConfig | PriceUpliftConfigResType,
) => {
  if (config.percentageType) {
    return config.percentageType;
  }
  const [firstProductConfig] = config.productConfigurations ?? [];
  return firstProductConfig?.percentageType;
};

/** Initial drawer form state for the quote's current configurations. */
export const deriveQuoteUpliftDrawerState = (
  eligibleOfferings: UpliftEligibleOffering[],
  summary: QuoteUpliftSummary,
): QuoteRenewalUpliftFormType => {
  // Uniform mode uplifts every product, so an uplift that does not already
  // reach all of them can only be shown — and saved back unchanged — with the
  // per-product checkboxes individual mode provides. `isPartial` is exactly
  // that question, and it is the same flag the header label renders as
  // "(partial)", so the two can never disagree about what the quote looks like.
  // It covers a whole offering left unconfigured as well as a configuration
  // that skips some of its own products: seeding uniform mode for either would
  // open the drawer already proposing to uplift the excluded products at the
  // header percentage, with no checkbox on screen to reveal it.
  const isPartialUplift = summary.status === 'uniform' && summary.isPartial;

  const firstConfiguredPercentageType = eligibleOfferings
    .map(({ config }) => config && getConfigPercentageType(config))
    .find(Boolean);

  const percentageType =
    (summary.status === 'uniform' ? summary.percentageType : undefined) ??
    firstConfiguredPercentageType ??
    PriceUpliftPercentageTypeEnum.FIXED;

  return {
    mode:
      summary.status === 'variable' || isPartialUplift
        ? 'individual'
        : 'uniform',
    amount: summary.status === 'uniform' ? summary.amount : null,
    percentageType,
    offerings: eligibleOfferings.map(
      ({ parentQuoteOffering, config, eligibleProductIds }) => ({
        quoteOfferingId: parentQuoteOffering.id,
        products: eligibleProductIds.map((productId) => {
          if (!config) {
            // Nothing configured means nothing will be uplifted, so the product
            // is excluded — the same rule the two configured branches below
            // follow, and the only one that survives a round trip. Unchecking
            // every product in an offering deletes its configuration, and a
            // deleted configuration is indistinguishable from one that never
            // existed: starting included would re-check all of them on reopen
            // and claim an uplift that is not going to be applied.
            //
            // The headline flow does not depend on this. Uniform mode uplifts
            // every product regardless of inclusion — the checkboxes are not
            // even rendered there — and switching into individual mode seeds
            // them all from the header percentage.
            return { productId, included: false, amount: null };
          }

          if (config.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS) {
            // An amount of 0 is no uplift, the same as it is for a
            // product-specific entry below and in the save plan.
            return {
              productId,
              included: isUpliftAmount(config.amount),
              amount: config.amount ?? null,
            };
          }

          const productConfig = (config.productConfigurations ?? []).find(
            (productConfiguration) =>
              productConfiguration.productId === productId,
          );

          return {
            productId,
            included: isUpliftAmount(productConfig?.amount),
            amount: productConfig?.amount ?? null,
          };
        }),
      }),
    ),
  };
};

type DesiredConfig = {
  amount: number | null;
  percentageType: PriceUpliftPercentageTypeEnum;
  type: PriceUpliftConfigTypeEnum;
  productConfigurations:
    | {
        productId: string;
        amount: number;
        percentageType: PriceUpliftPercentageTypeEnum;
      }[]
    | null;
};

/**
 * Products that should end up with an uplift, with the percentage each receives.
 * Uniform mode applies the single header percentage to every product — the
 * drawer hides its inclusion checkboxes there and says so.
 */
const getDesiredProductUplifts = (
  formState: QuoteRenewalUpliftFormType,
  offeringFormState:
    | QuoteRenewalUpliftFormType['offerings'][number]
    | undefined,
) => {
  if (!offeringFormState) {
    return [];
  }

  if (formState.mode === 'uniform') {
    if (!isUpliftAmount(formState.amount)) {
      return [];
    }
    return offeringFormState.products.map(({ productId }) => ({
      productId,
      amount: formState.amount as number,
    }));
  }

  return offeringFormState.products
    .filter(({ included, amount }) => included && isUpliftAmount(amount))
    .map(({ productId, amount }) => ({ productId, amount: amount as number }));
};

const getDesiredConfig = (
  formState: QuoteRenewalUpliftFormType,
  eligibleOffering: UpliftEligibleOffering,
): DesiredConfig | null => {
  const offeringFormState = formState.offerings.find(
    ({ quoteOfferingId }) =>
      quoteOfferingId === eligibleOffering.parentQuoteOffering.id,
  );
  const desiredUplifts = getDesiredProductUplifts(formState, offeringFormState);

  if (!desiredUplifts.length) {
    return null;
  }

  const [firstUplift] = desiredUplifts;
  const isEveryProductAtSameAmount =
    desiredUplifts.length === eligibleOffering.eligibleProductIds.length &&
    desiredUplifts.every(({ amount }) => amount === firstUplift.amount);

  if (isEveryProductAtSameAmount) {
    return {
      type: PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS,
      amount: firstUplift.amount,
      percentageType: formState.percentageType,
      productConfigurations: null,
    };
  }

  return {
    type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
    amount: null,
    percentageType: formState.percentageType,
    productConfigurations: desiredUplifts.map(({ productId, amount }) => ({
      productId,
      amount,
      percentageType: formState.percentageType,
    })),
  };
};

/**
 * The uplift each eligible product ends up with, in a form two configurations
 * can be compared by.
 *
 * Both configuration shapes expand to the same representation, so an existing
 * PRODUCT_SPECIFIC configuration that already uplifts every eligible product
 * matches the APPLY_TO_ALL_PRODUCTS form `getDesiredConfig` collapses to
 * instead of forcing a needless update — the shape every single-product
 * offering is stored in by the per-offering drawer.
 *
 * Only eligible products are compared, because they are the only ones the
 * drawer lists. A stored configuration can carry entries for products the form
 * cannot express — one-time products, or products absent from the ramp segment
 * on display — and `coversEveryProduct` already treats such an offering as
 * fully covered, so counting them here would report a pending change no edit
 * could ever resolve. They are left untouched rather than silently rewritten.
 */
const normalizeConfigForComparison = (
  config: DesiredConfig | PriceUpliftConfigResType,
  eligibleProductIds: string[],
) => {
  const isApplyToAll =
    config.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS;
  const upliftByProductId = new Map(
    (config.productConfigurations ?? []).map(
      ({ productId, amount, percentageType }) => [
        productId,
        { amount, percentageType },
      ],
    ),
  );

  return JSON.stringify(
    [...eligibleProductIds]
      .sort((left, right) => left.localeCompare(right))
      .flatMap((productId) => {
        // An APPLY_TO_ALL_PRODUCTS configuration may leave percentageType on
        // its product entries only — the same fallback the form seed uses.
        const uplift = isApplyToAll
          ? {
              amount: config.amount,
              percentageType: getConfigPercentageType(config),
            }
          : upliftByProductId.get(productId);

        if (!uplift || !isUpliftAmount(uplift.amount)) {
          return [];
        }
        return [
          {
            productId,
            amount: uplift.amount,
            percentageType:
              uplift.percentageType ?? PriceUpliftPercentageTypeEnum.FIXED,
          },
        ];
      }),
  );
};

const configsEqual = (
  existingConfig: PriceUpliftConfigResType,
  desiredConfig: DesiredConfig,
  eligibleProductIds: string[],
) =>
  normalizeConfigForComparison(existingConfig, eligibleProductIds) ===
  normalizeConfigForComparison(desiredConfig, eligibleProductIds);

/**
 * Diffs the drawer form state against the configurations already on the quote
 * and returns the create/update/delete calls needed to reconcile them.
 */
export const buildUpliftSavePlan = (
  formState: QuoteRenewalUpliftFormType,
  eligibleOfferings: UpliftEligibleOffering[],
): QuoteUpliftPlanStep[] => {
  const steps: QuoteUpliftPlanStep[] = [];

  eligibleOfferings.forEach((eligibleOffering) => {
    const { parentQuoteOffering, config } = eligibleOffering;
    const { priceUpliftConfigurationId } = parentQuoteOffering;

    // A locked configuration is already committed to a generated renewal quote.
    if (config?.locked) {
      return;
    }

    const desiredConfig = getDesiredConfig(formState, eligibleOffering);

    if (!desiredConfig) {
      if (priceUpliftConfigurationId) {
        steps.push({
          action: 'delete',
          quoteOfferingId: parentQuoteOffering.id,
          priceUpliftConfigurationId,
        });
      }
      return;
    }

    if (
      config &&
      configsEqual(config, desiredConfig, eligibleOffering.eligibleProductIds)
    ) {
      return;
    }

    const parsed = PriceUpliftConfigFormSchema.safeParse(desiredConfig);
    if (!parsed.success) {
      logger.error(
        `Unable to build renewal uplift payload for quote offering ${parentQuoteOffering.id}`,
        parsed.error,
      );
      return;
    }

    if (priceUpliftConfigurationId) {
      steps.push({
        action: 'update',
        quoteOfferingId: parentQuoteOffering.id,
        priceUpliftConfigurationId,
        payload: parsed.data,
      });
      return;
    }

    steps.push({
      action: 'create',
      quoteOfferingId: parentQuoteOffering.id,
      payload: parsed.data,
    });
  });

  return steps;
};
